// The about page, set as a book's own front matter: an author's note. It
// continues the library's printed idiom (Playfair masthead, Source Serif body,
// hairline rules) rather than importing a marketing page — the story it tells
// is why the product exists, and it reads best in the product's own type.
//
// The narrative runs in short titled chapters (roman numerals, small caps)
// and closes on a two-line pull quote — the pitch itself, an invitation to
// read. Its causal arc runs through the CHILDREN, not the founder: get kids
// reading voraciously → that takes a corpus of good readings → retelling the
// classics opens them to young readers WITHOUT simplifying the story (the
// sentence is retold, never the story). The founder's own reading history is
// deliberately NOT retold here — "Well Read" (The Canticle, 2019) is linked
// inline where it comes up, and the one photograph is the "joiner" from the
// WRTG 5990 capstone "Unsettling Erasure" (2020), linked from its caption.

import { Link } from "react-router";

/** A chapter opening: numeral and title in small caps over a hairline. */
function ChapterHeading({
  numeral,
  title,
}: {
  numeral: string;
  title: string;
}) {
  return (
    <h2 className="text-muted-foreground pt-6 text-center font-serif text-[0.75rem] tracking-[0.35em] uppercase">
      <span className="font-rockwell">{numeral}</span>
      <span aria-hidden="true"> · </span>
      {title}
    </h2>
  );
}

/** An inline prose link to an off-site piece — dotted underline, echoing the
 *  contents pages' dotted leaders. */
function ProseLink({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="decoration-foreground/40 hover:text-primary hover:decoration-primary underline decoration-dotted underline-offset-[0.25em] transition-colors"
    >
      {children}
    </a>
  );
}

export function AboutPage() {
  return (
    <div className="bg-background min-h-svh">
      <main className="mx-auto max-w-2xl px-6 pt-20 pb-16 sm:px-8">
        {/* Masthead — matches the library's title page. */}
        <header className="mb-12 text-center">
          <p className="text-muted-foreground font-serif text-sm tracking-[0.35em] uppercase">
            An author’s note
          </p>
          <h1 className="font-display text-foreground mt-3 text-5xl font-black tracking-tight sm:text-6xl">
            About Retells
          </h1>
          <div
            aria-hidden="true"
            className="border-border mx-auto mt-10 w-24 border-t"
          />
        </header>

        <article className="text-foreground space-y-6 font-serif text-[1.05rem] leading-relaxed">
          <ChapterHeading numeral="I" title="The Counting Sticks" />
          <p className="first-letter:font-display first-letter:float-left first-letter:mt-1 first-letter:pr-3 first-letter:text-[3.4rem] first-letter:leading-[0.8] first-letter:font-black">
            Retells began, though it didn’t have a name yet, with a failed
            experiment. In 2015 I was drafting a book for parents about how my
            daughter learned to read — a strange book for me to be writing,
            because I’m a numbers guy. I had planned to gift her early{" "}
            <em>mathematical</em> literacy: a counting language in base four,
            counting sticks, an abacus, an entire curriculum. She hated the
            counting sticks. What she wanted, standing there holding out a worn
            book: “Daddy, read to me.” So I resigned myself to being the only
            thing I could be — Daddy.
          </p>

          <ChapterHeading numeral="II" title="The Corpus" />
          <p>
            What followed was a decade of building for readers, chasing one
            goal: every child reading as voraciously as my daughter does. I
            wrote software that tracks, word by word, which words a reader knows
            on sight, and I built a corpus to feed it — more than a hundred
            thousand short readings, so it could hand any reader a text they
            could already read fluently. Fluency isn’t general, it’s specific:
            we each read the words we know, and we meet new ones best from
            inside a story we aren’t lost in. I know, because reading has never
            been an easy pastime for me — that story is{" "}
            <ProseLink href="https://rfugal.medium.com/well-read-2f23d614645d">
              Well Read
            </ProseLink>
            , a literacy narrative published in <em>The Canticle</em> in 2019.
          </p>

          <ChapterHeading numeral="III" title="Side by Side" />
          <p>
            Retells is corpus-building, still. An appetite like my daughter’s
            needs a deep shelf of good readings, and the best stories we have
            are the classics — walled off behind prose that stops young readers
            at the gate. So I retell them: plain, modern prose, set beside the
            original and synced line for line. What gets retold is the sentence,
            never the story. These are not simplifications — children can hold
            complex stories as well as any of us, and they deserve the real
            ones. When the original holds your attention, read the original.
            When it lets go, the retelling keeps you in the story until you’re
            ready to cross back. I built this shelf for young readers; it caught
            a reader I didn’t expect — me.
          </p>

          <ChapterHeading numeral="IV" title="The Cutting Room Floor" />
          <p>
            A retelling starts with character and narrative studies —
            comprehensive, graduate-level book reports — and then a long run of
            decisions about what serves the story and what lands on the cutting
            room floor. That is the easy part. The hard part, for me, is
            rewording what survives into second- and third-grade English, so I
            built a word processor’s thesaurus on steroids: editing tools that
            map a reader’s sight vocabulary and help an author stay inside it.
            To retell the Odyssey I read it, and reread it, very closely. There
            was real joy in the character studies, real joy in pulling the
            narrative apart to see how it holds together.
          </p>

          <figure>
            <img
              src="/retelling-desk.jpg"
              alt="A worktable covered in printed pages and hundreds of paper strips of text, cut apart and sorted into clusters, with glue sticks and a pen among them"
              width={1600}
              height={900}
              loading="lazy"
              decoding="async"
              className="w-full rounded-[3px] ring-1 ring-black/20"
            />
            <figcaption className="text-muted-foreground mt-3 text-center font-serif text-sm italic">
              The joiner from{" "}
              <ProseLink href="https://smart-knowledge-systems.com/portfolio?tags=colonial-studies&courses=WRTG+5990">
                Unsettling Erasure
              </ProseLink>{" "}
              (2020): fragments of hundreds of pages, coalesced by hand into the
              shape of my thinking. A retelling’s studies get the same treatment
              — with less literal cutting and pasting.
            </figcaption>
          </figure>
        </article>

        {/* The climactic close — the pitch itself, set centered in the
            display face. */}
        <blockquote className="mt-14 text-center">
          <p className="font-display text-foreground text-2xl leading-snug font-medium tracking-tight text-balance">
            In the end, a good story is simply enjoyable.
          </p>
          <p className="font-display text-primary mt-3 text-2xl leading-snug font-black tracking-tight text-balance">
            Come read.
          </p>
        </blockquote>

        <p className="text-muted-foreground mt-10 text-right font-serif text-[1.05rem] italic">
          — Russ T. Fugal
        </p>

        <div className="mt-14 text-center">
          <Link
            to="/"
            className="border-foreground text-foreground hover:border-primary hover:text-primary border-b pb-1 font-serif text-sm tracking-[0.2em] uppercase transition-colors"
          >
            Browse the shelf
          </Link>
        </div>
      </main>
    </div>
  );
}
